import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  SkeletonRows,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { MapPin, Server, Users } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

interface LoadingAddress {
  id: string;
  label: string;
  line1: string;
  city: string;
  country: string;
  active: boolean;
}

interface Subcontractor {
  id: string;
  name: string;
  role: string | null;
  country: string | null;
  nextReviewAt: string | null;
}

interface SubProcessor {
  id: string;
  name: string;
  purpose: string;
  location: string | null;
}

interface PostError {
  error?: string;
}

function errMsg(res: { ok: boolean; status: number; data: PostError }, fallback: string): string {
  return res.data.error ?? fallback;
}

function LoadingAddresses() {
  const { t } = useTranslation('registry');
  const { toast } = useToast();
  const [items, setItems] = useState<LoadingAddress[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await api.get<{ items: LoadingAddress[] }>('/api/registry/loading-addresses');
    if (!res.ok) {
      setError(t('loadingAddresses.loadError', { status: res.status }));
      setItems([]);
      return;
    }
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: LoadingAddress[] }>('/api/registry/loading-addresses');
      if (!active) return;
      if (!res.ok) {
        setError(t('loadingAddresses.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<PostError>('/api/registry/loading-addresses', {
        label,
        line1,
        city,
        postalCode,
        country,
      });
      if (!res.ok) {
        const msg = errMsg(res, t('errors.requestFailed', { status: res.status }));
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setLabel('');
      setLine1('');
      setCity('');
      setPostalCode('');
      setCountry('');
      toast(t('toast.addressAdded'));
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={t('loadingAddresses.heading')}
        subtitle={t('loadingAddresses.description')}
      />
      <CardBody>
        <form
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('loadingAddresses.fields.label')} htmlFor="la-label">
            <Input
              id="la-label"
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <Field label={t('loadingAddresses.fields.line1')} htmlFor="la-line1">
            <Input
              id="la-line1"
              required
              value={line1}
              onChange={(e) => setLine1(e.target.value)}
            />
          </Field>
          <Field label={t('loadingAddresses.fields.city')} htmlFor="la-city">
            <Input id="la-city" required value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label={t('loadingAddresses.fields.postalCode')} htmlFor="la-postal">
            <Input
              id="la-postal"
              required
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </Field>
          <Field label={t('loadingAddresses.fields.country')} htmlFor="la-country">
            <Input
              id="la-country"
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('saving') : t('loadingAddresses.submit')}
          </Button>
        </form>
        {formError && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}

        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MapPin className="h-6 w-6" />}
            title={t('loadingAddresses.empty.title')}
            hint={t('loadingAddresses.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('loadingAddresses.columns.label')}</Th>
                <Th>{t('loadingAddresses.columns.address')}</Th>
                <Th>{t('loadingAddresses.columns.city')}</Th>
                <Th>{t('loadingAddresses.columns.country')}</Th>
                <Th>{t('loadingAddresses.columns.active')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((a) => (
                <Tr key={a.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">{a.label}</Td>
                  <Td>{a.line1}</Td>
                  <Td>{a.city}</Td>
                  <Td>{a.country}</Td>
                  <Td>
                    <Badge tone={a.active ? 'green' : 'slate'}>
                      {a.active ? t('yes') : t('no')}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

function Subcontractors() {
  const { t, i18n } = useTranslation('registry');
  const { toast } = useToast();
  const [items, setItems] = useState<Subcontractor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await api.get<{ items: Subcontractor[] }>('/api/registry/subcontractors');
    if (!res.ok) {
      setError(t('subcontractors.loadError', { status: res.status }));
      setItems([]);
      return;
    }
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: Subcontractor[] }>('/api/registry/subcontractors');
      if (!active) return;
      if (!res.ok) {
        setError(t('subcontractors.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<PostError>('/api/registry/subcontractors', {
        name,
        ...(role ? { role } : {}),
        ...(country ? { country } : {}),
      });
      if (!res.ok) {
        const msg = errMsg(res, t('errors.requestFailed', { status: res.status }));
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setName('');
      setRole('');
      setCountry('');
      toast(t('toast.subcontractorAdded'));
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('subcontractors.heading')} subtitle={t('subcontractors.description')} />
      <CardBody>
        <form
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('subcontractors.fields.name')} htmlFor="sc-name">
            <Input id="sc-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('subcontractors.fields.role')} htmlFor="sc-role">
            <Input id="sc-role" value={role} onChange={(e) => setRole(e.target.value)} />
          </Field>
          <Field label={t('subcontractors.fields.country')} htmlFor="sc-country">
            <Input id="sc-country" value={country} onChange={(e) => setCountry(e.target.value)} />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('saving') : t('subcontractors.submit')}
          </Button>
        </form>
        {formError && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}

        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title={t('subcontractors.empty.title')}
            hint={t('subcontractors.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('subcontractors.columns.name')}</Th>
                <Th>{t('subcontractors.columns.role')}</Th>
                <Th>{t('subcontractors.columns.country')}</Th>
                <Th>{t('subcontractors.columns.nextReview')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((s) => (
                <Tr key={s.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">{s.name}</Td>
                  <Td>{s.role ?? '—'}</Td>
                  <Td>{s.country ?? '—'}</Td>
                  <Td>{formatDate(s.nextReviewAt, i18n.language)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

function SubProcessors() {
  const { t } = useTranslation('registry');
  const { toast } = useToast();
  const [items, setItems] = useState<SubProcessor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await api.get<{ items: SubProcessor[] }>('/api/registry/sub-processors');
    if (!res.ok) {
      setError(t('subProcessors.loadError', { status: res.status }));
      setItems([]);
      return;
    }
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: SubProcessor[] }>('/api/registry/sub-processors');
      if (!active) return;
      if (!res.ok) {
        setError(t('subProcessors.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<PostError>('/api/registry/sub-processors', { name, purpose });
      if (!res.ok) {
        const msg = errMsg(res, t('errors.requestFailed', { status: res.status }));
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setName('');
      setPurpose('');
      toast(t('toast.subProcessorAdded'));
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('subProcessors.heading')} subtitle={t('subProcessors.description')} />
      <CardBody>
        <form
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('subProcessors.fields.name')} htmlFor="sp-name">
            <Input id="sp-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('subProcessors.fields.purpose')} htmlFor="sp-purpose" className="flex-1">
            <Input
              id="sp-purpose"
              required
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('saving') : t('subProcessors.submit')}
          </Button>
        </form>
        {formError && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}

        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Server className="h-6 w-6" />}
            title={t('subProcessors.empty.title')}
            hint={t('subProcessors.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('subProcessors.columns.name')}</Th>
                <Th>{t('subProcessors.columns.purpose')}</Th>
                <Th>{t('subProcessors.columns.location')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((s) => (
                <Tr key={s.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">{s.name}</Td>
                  <Td>{s.purpose}</Td>
                  <Td>{s.location ?? '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

export default function Registry() {
  const { t } = useTranslation('registry');
  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-6">
        <LoadingAddresses />
        <Subcontractors />
        <SubProcessors />
      </div>
    </AppLayout>
  );
}
